import { SpawnControlRow } from '../../../shared/ui';
import type { ArcadeHudViewProps } from './types';
import './styles.css';

/**
 * Pure UI: in-run weapon HUD, top-right stack, spawn tools, lock reticles.
 * All handlers and feature flags are passed in from the app shell.
 */
export function ArcadeHudInterface(props: ArcadeHudViewProps) {
  const {
    sandboxLike,
    activeWeapon,
    missileLabel,
    secondaryWeaponLabel,
    countermeasureLabel,
    ammoIcons,
    railAmmo,
    chaffAmmo,
    missileMax,
    railMax,
    chaffMax,
    badge,
    pauseUi,
    showSettings,
    showSandboxTools,
    showEnemySpawn,
    showTeammateSpawn,
    showImmortal,
    showObstacleControls,
    enemyAlive,
    enemySpawnCount,
    enemySpawnMax,
    teammateAlive,
    teammateSpawnCount,
    teammateSpawnMax,
    immortal,
    blockAutoAim,
    obstaclesEnabled,
    obstacleLimit,
    obstacleLimitMin,
    obstacleLimitMax,
    refScoreValue,
    refObstacleLimitLabel,
    refMissileLock,
    refEnemyLock,
    refThreatLock,
    onSelectMissile,
    onSelectRailgun,
    onSelectChaff,
    onTogglePause,
    onToggleSettings,
    onEnemySpawnCount,
    onTeammateSpawnCount,
    onSpawnEnemy,
    onSpawnTeammate,
    onToggleImmortal,
    onToggleObstacles,
    onObstacleLimit,
    getObstacleCount,
  } = props;

  const pausedLike = pauseUi === 'prompt' || pauseUi === 'countdown';

  return (
    <>
      <button
        type="button"
        className={[
          'arcade-ammo-hud',
          activeWeapon === 'missile' ? 'arcade-weapon-hud--active' : '',
          sandboxLike ? 'arcade-weapon-hud--sandbox' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={`Select ${missileLabel.toLowerCase()}`}
        onClick={(event) => {
          event.stopPropagation();
          onSelectMissile();
        }}
      >
        <span className="arcade-ammo-hud__label">{missileLabel}</span>
        {sandboxLike ? (
          <span className="arcade-infinite-glyph">∞</span>
        ) : (
          <>
            <div className="arcade-ammo-hud__stack" aria-hidden>
              {ammoIcons.map((filled, i) => (
                <span
                  key={i}
                  className={[
                    'arcade-missile-icon',
                    `arcade-missile-icon--slot-${i}`,
                    filled ? 'arcade-missile-icon--filled' : 'arcade-missile-icon--empty',
                  ].join(' ')}
                />
              ))}
            </div>
            <span className="arcade-weapon-hud__max">/{missileMax}</span>
          </>
        )}
      </button>
      <button
        type="button"
        className={[
          'arcade-railgun-hud',
          secondaryWeaponLabel === 'MINIGUN' ? 'arcade-railgun-hud--minigun' : '',
          activeWeapon === 'railgun' ? 'arcade-weapon-hud--active' : '',
          sandboxLike ? 'arcade-weapon-hud--sandbox' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={`Select ${secondaryWeaponLabel.toLowerCase()}`}
        onClick={(event) => {
          event.stopPropagation();
          onSelectRailgun();
        }}
      >
        <span className="arcade-railgun-hud__label">{secondaryWeaponLabel}</span>
        {sandboxLike ? (
          <span className="arcade-infinite-glyph">∞</span>
        ) : (
          <>
            <div className="arcade-rail-bar" aria-hidden>
              {Array.from({ length: railMax }, (_, i) => (
                <span
                  key={i}
                  className={[
                    'arcade-rail-tick',
                    i < railAmmo ? 'arcade-rail-tick--filled' : 'arcade-rail-tick--empty',
                  ].join(' ')}
                />
              ))}
            </div>
            <span className="arcade-weapon-hud__max">/{railMax}</span>
          </>
        )}
      </button>
      <button
        type="button"
        className={[
          'arcade-chaff-hud',
          countermeasureLabel === 'FLARES' ? 'arcade-chaff-hud--flares' : '',
          activeWeapon === 'chaff' ? 'arcade-weapon-hud--active' : '',
          sandboxLike ? 'arcade-weapon-hud--sandbox' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={`Select ${countermeasureLabel.toLowerCase()}`}
        onClick={(event) => {
          event.stopPropagation();
          onSelectChaff();
        }}
      >
        <span className="arcade-chaff-hud__label">{countermeasureLabel}</span>
        {sandboxLike ? (
          <span className="arcade-infinite-glyph">∞</span>
        ) : (
          <>
            <div className="arcade-chaff-bar" aria-hidden>
              {Array.from({ length: chaffMax }, (_, i) => (
                <span
                  key={i}
                  className={[
                    'arcade-chaff-pip',
                    i < chaffAmmo ? 'arcade-chaff-pip--filled' : 'arcade-chaff-pip--empty',
                  ].join(' ')}
                />
              ))}
            </div>
            <span className="arcade-weapon-hud__max">/{chaffMax}</span>
          </>
        )}
      </button>
      <div className="arcade-hud-top-right">
        {badge ? (
          <div
            className={[
              'arcade-sandbox-home-label',
              'arcade-sandbox-home-label--hud',
              badge === 'DEV' ? 'arcade-sandbox-home-label--dev' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label={`${badge} mode`}
          >
            <span className="arcade-sandbox-home-label__corner arcade-sandbox-home-label__corner--tl" />
            <span className="arcade-sandbox-home-label__corner arcade-sandbox-home-label__corner--tr" />
            <span className="arcade-sandbox-home-label__corner arcade-sandbox-home-label__corner--bl" />
            <span className="arcade-sandbox-home-label__corner arcade-sandbox-home-label__corner--br" />
            <span className="arcade-sandbox-home-label__text">{badge}</span>
          </div>
        ) : null}
        <div className="arcade-hud-icon-row">
          <button
            type="button"
            className={
              pausedLike ? 'arcade-hud-icon-btn arcade-hud-icon-btn--active' : 'arcade-hud-icon-btn'
            }
            aria-label={pausedLike ? 'Resume game' : 'Pause game'}
            title={pausedLike ? 'Resume' : 'Pause'}
            disabled={pauseUi === 'countdown'}
            onClick={(event) => {
              event.stopPropagation();
              onTogglePause();
            }}
          >
            {pausedLike ? (
              <svg
                className="arcade-hud-icon-btn__icon arcade-hud-icon-btn__icon--fill"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path d="M8 5.5v13l11-6.5-11-6.5z" />
              </svg>
            ) : (
              <svg
                className="arcade-hud-icon-btn__icon arcade-hud-icon-btn__icon--fill"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <rect x="6" y="5" width="4" height="14" rx="0.5" />
                <rect x="14" y="5" width="4" height="14" rx="0.5" />
              </svg>
            )}
          </button>
          <button
            type="button"
            className={
              showSettings
                ? 'arcade-hud-icon-btn arcade-hud-icon-btn--active'
                : 'arcade-hud-icon-btn'
            }
            aria-label="Open settings"
            title="Settings"
            onClick={(event) => {
              event.stopPropagation();
              onToggleSettings();
            }}
          >
            <svg
              className="arcade-hud-icon-btn__icon arcade-hud-icon-btn__icon--stroke"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
        </div>
        {showSandboxTools ? (
          <>
            {showEnemySpawn ? (
              <SpawnControlRow
                kind="enemy"
                rowClassName="arcade-spawn-enemy-row"
                count={enemySpawnCount}
                max={enemySpawnMax}
                unitAlive={enemyAlive}
                onCountChange={onEnemySpawnCount}
                onSpawn={onSpawnEnemy}
                isolatePointer
              />
            ) : null}
            {showTeammateSpawn ? (
              <SpawnControlRow
                kind="teammate"
                rowClassName="arcade-spawn-teammate-row"
                count={teammateSpawnCount}
                max={teammateSpawnMax}
                unitAlive={teammateAlive}
                onCountChange={onTeammateSpawnCount}
                onSpawn={onSpawnTeammate}
                isolatePointer
              />
            ) : null}
            {showImmortal ? (
              <button
                type="button"
                className={immortal ? 'arcade-immortal arcade-immortal--on' : 'arcade-immortal'}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleImmortal();
                }}
              >
                {immortal ? 'IMMORTAL ON' : 'IMMORTAL'}
              </button>
            ) : null}
            {showObstacleControls ? (
              <>
                <button
                  type="button"
                  className="arcade-stop-obstacles"
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleObstacles();
                  }}
                >
                  {obstaclesEnabled ? 'STOP OBSTACLES' : 'START OBSTACLES'}
                </button>
                <label
                  className="arcade-obstacle-limit"
                  onClick={(event) => event.stopPropagation()}
                  onMouseDown={(event) => event.stopPropagation()}
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  <span className="arcade-obstacle-limit__label" ref={refObstacleLimitLabel}>
                    BLOCKS 0/{obstacleLimit}
                  </span>
                  <input
                    type="range"
                    className="arcade-obstacle-limit__slider"
                    min={obstacleLimitMin}
                    max={obstacleLimitMax}
                    step={1}
                    value={obstacleLimit}
                    aria-label="Maximum obstacle blocks"
                    onChange={(event) => {
                      const next = Number(event.target.value);
                      onObstacleLimit(next);
                      const label = refObstacleLimitLabel.current;
                      if (label) {
                        label.textContent = `BLOCKS ${getObstacleCount()}/${next}`;
                      }
                    }}
                  />
                </label>
              </>
            ) : null}
          </>
        ) : (
          <div className="arcade-score-hud arcade-score-hud--in-stack" aria-live="off">
            <span className="arcade-score-hud__label">SCORE</span>
            <span className="arcade-score-hud__value" ref={refScoreValue}>
              0
            </span>
          </div>
        )}
      </div>
      {/* Missile block lock (green) */}
      <div
        ref={refMissileLock}
        className="arcade-missile-lock"
        style={{ display: 'none' }}
        aria-hidden
      >
        <span className="arcade-missile-lock__corner arcade-missile-lock__corner--tl" />
        <span className="arcade-missile-lock__corner arcade-missile-lock__corner--tr" />
        <span className="arcade-missile-lock__corner arcade-missile-lock__corner--bl" />
        <span className="arcade-missile-lock__corner arcade-missile-lock__corner--br" />
      </div>
      {/* Enemy soft (orange) / hard (red) lock */}
      <div
        ref={refEnemyLock}
        className="arcade-enemy-lock arcade-enemy-lock--soft"
        style={{ display: 'none' }}
        aria-hidden
      >
        <span className="arcade-enemy-lock__corner arcade-enemy-lock__corner--tl" />
        <span className="arcade-enemy-lock__corner arcade-enemy-lock__corner--tr" />
        <span className="arcade-enemy-lock__corner arcade-enemy-lock__corner--bl" />
        <span className="arcade-enemy-lock__corner arcade-enemy-lock__corner--br" />
        <span className="arcade-enemy-lock__ticks arcade-enemy-lock__ticks--left">
          {Array.from({ length: 8 }, (_, i) => (
            <span key={`L${i}`} className="arcade-enemy-lock__tick" data-tick={i} />
          ))}
        </span>
        <span className="arcade-enemy-lock__ticks arcade-enemy-lock__ticks--right">
          {Array.from({ length: 8 }, (_, i) => (
            <span key={`R${i}`} className="arcade-enemy-lock__tick" data-tick={i} />
          ))}
        </span>
      </div>
      {/* Hostile missile lock-on warning (shared UI for any locking foe) */}
      <div
        ref={refThreatLock}
        className="arcade-threat-lock"
        style={{ display: 'none' }}
        aria-live="assertive"
        role="status"
      >
        <span className="arcade-threat-lock__corner arcade-threat-lock__corner--tl" />
        <span className="arcade-threat-lock__corner arcade-threat-lock__corner--tr" />
        <span className="arcade-threat-lock__corner arcade-threat-lock__corner--bl" />
        <span className="arcade-threat-lock__corner arcade-threat-lock__corner--br" />
        <div className="arcade-threat-lock__label">
          <span className="arcade-threat-lock__text">LOCK</span>
          <span className="arcade-threat-lock__bar" aria-hidden>
            <span className="arcade-threat-lock__bar-fill" data-threat-fill />
          </span>
        </div>
      </div>
      <div className="arcade-status-badges" aria-live="polite">
        {immortal ? <div className="arcade-immortal-badge">IMMORTAL</div> : null}
        <div
          className={
            blockAutoAim
              ? 'arcade-auto-aim-badge arcade-auto-aim-badge--on'
              : 'arcade-auto-aim-badge arcade-auto-aim-badge--off'
          }
        >
          {blockAutoAim ? 'AUTO-AIM ON' : 'AUTO-AIM OFF'}
        </div>
      </div>
    </>
  );
}
